import { useState } from "react";
import {
  Button,
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  RadioCards,
  ReasonField,
  TextField,
  isApiError,
  useCommandMutation,
  useReason,
  useToast,
} from "@amber/ui";
import type { QuickAction, TaskRow } from "../../contracts/index";
import { operationsCommands } from "../queries";
import { AFTER_OPERATIONS_CHANGE, AFTER_OPERATIONS_CONFLICT } from "../keys";

type Field = QuickAction["fields"][number];

function initialValues(fields: readonly Field[]): Record<string, string> {
  return Object.fromEntries(
    fields.flatMap((f) => (f.kind === "choice" && f.options?.[0] ? [[f.name, f.options[0].value]] : [])),
  );
}

function ActionField({
  field,
  value,
  error,
  onChange,
}: {
  field: Field;
  value: string;
  error: string | null;
  onChange: (value: string) => void;
}) {
  if (field.kind === "choice")
    return (
      <RadioCards label={field.label} value={value} options={field.options ?? []} onChange={onChange} error={error} />
    );
  return (
    <TextField
      label={field.label}
      required
      type={field.kind === "date" ? "date" : "text"}
      {...(field.kind === "money" ? { inputMode: "decimal" as const } : {})}
      value={value}
      error={error}
      onChange={(e) => {
        onChange(e.target.value);
      }}
    />
  );
}

export function QuickActionDialog({
  task,
  action,
  onClose,
}: {
  task: TaskRow;
  action: QuickAction;
  onClose: () => void;
}) {
  const toast = useToast();
  const [values, setValues] = useState<Record<string, string>>(() => initialValues(action.fields));
  const mutation = useCommandMutation({
    run: (csrf, text: string) =>
      operationsCommands.quickAction(task.id, action.key, { base_revision: task.revision, reason: text, values }, csrf),
    invalidate: AFTER_OPERATIONS_CHANGE,
    invalidateOnConflict: AFTER_OPERATIONS_CONFLICT,
    onSuccess: (result) => {
      toast.show(result.closed ? "Task moved to Done" : "Done");
      onClose();
    },
  });
  const fieldErrors = isApiError(mutation.error) ? mutation.error.fieldErrors() : {};
  const conflict = isApiError(mutation.error) && mutation.error.code === "conflict";
  const reason = useReason(fieldErrors["reason"]);
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={action.label.replace(/…$/, "")}
      onSubmit={() => {
        if (reason.submit()) mutation.mutate(reason.trimmed);
      }}
      footer={
        conflict ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <DialogFooter
            onClose={onClose}
            confirm={action.label.replace(/…$/, "")}
            pending={mutation.isPending}
            failed={mutation.isError}
          />
        )
      }
    >
      <DialogText>
        <b>{task.title}</b> · {task.context}
      </DialogText>
      {action.fields.map((f) => (
        <ActionField
          key={f.name}
          field={f}
          value={values[f.name] ?? ""}
          error={
            isApiError(mutation.error)
              ? (mutation.error.issues.find((i) => i.path.endsWith(f.name))?.message ?? null)
              : null
          }
          onChange={(v) => {
            setValues({ ...values, [f.name]: v });
          }}
        />
      ))}
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      <FailedCallout error={mutation.error} fieldsShown={!conflict} />
    </Dialog>
  );
}
